/**
 * OfertaShell — fixed-overlay c оферой (публичным пользовательским
 * соглашением), показывается сразу после онбординга.
 *
 * Правила показа:
 *   — user.ofertaVersionAccepted === currentVersion → unmount навсегда
 *     в этой сессии (возвращаемся к обычному флоу).
 *   — иначе ждём 550мс после события `luna:onboarding-done` (чтобы
 *     фейд онбординга отыграл) и открываем эту оферту fade-in.
 *   — если пользователь уже прошёл онбординг в прошлом (overlay
 *     онбординга вообще не монтируется), но оферта ещё не принята —
 *     показываем оферту сразу при монтировании, без задержки.
 *
 * Взаимодействие:
 *   — Кнопка «Продолжить» активируется, когда выполнены ОБА условия:
 *     1) юзер докрутил страницу до конца;
 *     2) поставил галочку «Я ознакомился».
 *   — Клик → POST /api/luna/oferta/accept → refreshAuth →
 *     fade-out → unmount.
 *
 * z-index 9100 — на слой выше онбординга (9000), чтобы если их
 * монтирует в один момент, оферта поверх.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { lunaApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { ChevronDown } from 'lucide-react';
import { OfertaBackdrop } from './OfertaBackdrop';
import { OfertaDocument } from './OfertaDocument';

type Phase =
  | 'loading'
  | 'waiting-onboarding'
  | 'active'
  | 'accepting'
  | 'closing'
  | 'done';

const SCROLL_TOLERANCE_PX = 48;

export function OfertaShell() {
  const { t } = useTranslation();
  const { user, refresh: refreshAuth } = useAuth();
  const { haptic } = useTelegramWebApp();

  const [phase, setPhase] = useState<Phase>('loading');
  const [content, setContent] = useState<string | null>(null);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  // ── 1. На маунте: решаем, должна ли оферта вообще показаться ───────
  useEffect(() => {
    if (!user?.id) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await lunaApi.oferta.status();
        if (cancelled) return;
        if (!res.data.success) {
          setPhase('done');
          return;
        }
        if (!res.data.shouldShow) {
          setPhase('done');
          return;
        }
        setCurrentVersion(res.data.currentVersion);

        // Грузим сам текст параллельно — чтобы к моменту открытия
        // ничего не мигало.
        const c = await lunaApi.oferta.content();
        if (cancelled) return;
        if (c.data.success) {
          setContent(c.data.content);
        }

        // Решаем: показать сразу, или дождаться события онбординга.
        // Если онбординг ещё не сделан, OnboardingShell смонтирован
        // и показывается ПОВЕРХ нас — дождёмся его закрытия.
        const needsOnboarding = await needsOnboardingCheck();
        if (cancelled) return;

        if (needsOnboarding) {
          setPhase('waiting-onboarding');
        } else {
          setPhase('active');
        }
      } catch (err) {
        if (cancelled) return;
        setPhase('done');
        setError(err instanceof Error ? err.message : t('oferta.loadError'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // ── 2. Слушаем событие завершения онбординга ────────────────────────
  useEffect(() => {
    if (phase !== 'waiting-onboarding') return;
    const handler = () => {
      // Небольшая задержка чтобы closing-fade онбординга отыграл.
      window.setTimeout(() => setPhase('active'), 550);
    };
    window.addEventListener('luna:onboarding-done', handler);
    return () => window.removeEventListener('luna:onboarding-done', handler);
  }, [phase]);

  // ── 3. Scroll watcher: отмечаем достижение низа документа ──────────
  useEffect(() => {
    if (phase !== 'active') return;
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (gap <= SCROLL_TOLERANCE_PX) {
        setScrolledToEnd(true);
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    // Кик при маунте на случай, если документ короче экрана
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [phase, content]);

  // ── 4. Accept handler ──────────────────────────────────────────────
  const handleAccept = useCallback(async () => {
    if (!agreed || !scrolledToEnd) return;
    if (phase !== 'active') return;
    setPhase('accepting');
    haptic.success();
    try {
      await lunaApi.oferta.accept();
      await refreshAuth();
      setPhase('closing');
      window.setTimeout(() => {
        setPhase('done');
        // Оповещаем CoachMarkTour что оферта принята — можно
        // запускать подсветку элементов.
        window.dispatchEvent(new CustomEvent('luna:oferta-accepted'));
      }, 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('oferta.acceptError'));
      setPhase('active');
    }
  }, [agreed, scrolledToEnd, phase, haptic, refreshAuth]);

  const canSubmit = agreed && scrolledToEnd && phase === 'active';
  const readProgress = useReadProgress(scrollRef, phase);

  // Не рендерим оверлей пока загружаемся или уже приняли.
  if (phase === 'loading' || phase === 'done' || !content) return null;

  return (
    <AnimatePresence>
      {phase !== 'closing' && (
        <motion.div
          key="oferta-overlay"
          initial={{ opacity: 0 }}
          animate={{
            opacity: phase === 'waiting-onboarding' ? 0 : 1,
          }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.55, ease: [0.32, 0.72, 0, 1] }}
          className="fixed inset-0 z-[9100] flex flex-col overflow-hidden"
          style={{
            pointerEvents: phase === 'waiting-onboarding' ? 'none' : 'auto',
          }}
        >
          <OfertaBackdrop />

          {/* Scrollable document body */}
          <div
            ref={scrollRef}
            className="relative z-10 flex-1 overflow-y-auto overscroll-contain"
            style={{
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <OfertaDocument content={content} />
            {/* Нижний пин — гарантирует что при докрутке scrolledToEnd точно
                сработает, плюс служит якорем «вы всё прочитали». */}
            <EndMarker visible={!scrolledToEnd} />
          </div>

          {/* Right-edge read progress */}
          <ReadProgressRail progress={readProgress} />

          {/* Sticky accept bar */}
          <AcceptBar
            agreed={agreed}
            onToggleAgreed={() => {
              haptic.selection();
              setAgreed((v) => !v);
            }}
            scrolledToEnd={scrolledToEnd}
            canSubmit={canSubmit}
            submitting={phase === 'accepting'}
            onSubmit={handleAccept}
            version={currentVersion}
            error={error}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ──────────────────────────────────────────────────────────────────────
// AcceptBar — sticky bottom панель с чекбоксом + кнопкой
// ──────────────────────────────────────────────────────────────────────

interface AcceptBarProps {
  agreed: boolean;
  onToggleAgreed: () => void;
  scrolledToEnd: boolean;
  canSubmit: boolean;
  submitting: boolean;
  onSubmit: () => void;
  version: string | null;
  error: string | null;
}

function AcceptBar({
  agreed,
  onToggleAgreed,
  scrolledToEnd,
  canSubmit,
  submitting,
  onSubmit,
  version,
  error,
}: AcceptBarProps) {
  const { t } = useTranslation();
  return (
    <div
      className="relative z-20 shrink-0 border-t border-[rgba(255,255,255,0.08)]"
      style={{
        paddingBottom: 'max(env(safe-area-inset-bottom), 12px)',
        background:
          'linear-gradient(180deg, rgba(8,10,18,0.78) 0%, rgba(8,10,18,0.94) 60%, rgba(8,10,18,0.99) 100%)',
        backdropFilter: 'blur(24px) saturate(140%)',
        WebkitBackdropFilter: 'blur(24px) saturate(140%)',
      }}
    >
      <div className="mx-auto max-w-[680px] px-6 pb-3 pt-5">
        {/* Hint bar above checkbox */}
        <div className="mb-3 flex items-center justify-between text-[11px] uppercase tracking-[0.2em]">
          <span
            className={`flex items-center gap-1.5 font-semibold transition-colors ${
              scrolledToEnd
                ? 'text-[rgba(255,255,255,0.45)]'
                : 'text-[var(--color-lunar-500)]'
            }`}
          >
            {scrolledToEnd ? t('oferta.documentRead') : t('oferta.scrollToEnd')}
            {!scrolledToEnd && (
              <motion.span
                aria-hidden
                animate={{ y: [0, 4, 0] }}
                transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut' }}
                className="inline-flex"
              >
                <ChevronDown size={14} strokeWidth={2.8} />
              </motion.span>
            )}
          </span>
          {version && (
            <span className="font-mono text-[10px] text-[rgba(255,255,255,0.45)]">
              {t('oferta.editVersion', { version })}
            </span>
          )}
        </div>

        {/* Checkbox row */}
        <button
          type="button"
          onClick={onToggleAgreed}
          className="group flex w-full items-start gap-3 rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)] px-4 py-3 text-left transition-colors active:bg-[rgba(255,255,255,0.06)]"
        >
          <span
            className="mt-[2px] inline-flex size-5 shrink-0 items-center justify-center rounded-md border transition-all"
            style={{
              borderColor: agreed
                ? 'var(--color-lunar-500)'
                : 'rgba(255,255,255,0.25)',
              background: agreed
                ? 'linear-gradient(135deg, rgba(245,217,126,0.9) 0%, rgba(212,184,92,0.75) 100%)'
                : 'rgba(255,255,255,0.04)',
              boxShadow: agreed
                ? '0 0 0 4px rgba(245,217,126,0.12), inset 0 1px 0 rgba(255,255,255,0.4)'
                : 'none',
            }}
          >
            {agreed && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M2 6.3 L5 9.3 L10 3"
                  stroke="#0b0d16"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
          <span className="font-serif text-[14px] leading-[1.55] text-[rgba(255,255,255,0.9)]">
            {t('oferta.acceptCheckbox')}
          </span>
        </button>

        {/* Continue button */}
        <button
          type="button"
          disabled={!canSubmit}
          onClick={onSubmit}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 font-display text-[15px] font-semibold uppercase tracking-[0.16em] transition-all active:scale-[0.98]"
          style={{
            background: canSubmit
              ? 'linear-gradient(135deg, #f5d97e 0%, #d4b85c 100%)'
              : 'rgba(255,255,255,0.04)',
            color: canSubmit ? '#0b0d16' : 'rgba(255,255,255,0.35)',
            boxShadow: canSubmit
              ? '0 12px 36px -10px rgba(245,217,126,0.55), inset 0 1px 0 rgba(255,255,255,0.45)'
              : 'none',
            cursor: canSubmit ? 'pointer' : 'not-allowed',
          }}
        >
          {submitting ? (
            <span className="inline-flex items-center gap-2">
              <span className="inline-block size-3 animate-spin rounded-full border-2 border-current border-r-transparent" />
              {t('oferta.processing')}
            </span>
          ) : (
            <>
              {t('oferta.continue')}
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M3 7 L11 7 M7 3 L11 7 L7 11"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </>
          )}
        </button>

        {error && (
          <div className="mt-3 rounded-lg border border-[rgba(255,90,90,0.3)] bg-[rgba(255,90,90,0.08)] px-3 py-2 text-[12px] text-[rgba(255,200,200,0.9)]">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// ReadProgressRail — тонкая лунная полоска на правом краю
// ──────────────────────────────────────────────────────────────────────

function ReadProgressRail({ progress }: { progress: number }) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute right-[2px] top-[12vh] z-20 hidden h-[60vh] w-[2px] sm:block"
    >
      <div
        className="absolute inset-0 rounded-full"
        style={{ background: 'rgba(255,255,255,0.08)' }}
      />
      <div
        className="absolute left-0 top-0 w-full rounded-full transition-[height] duration-200"
        style={{
          height: `${Math.max(0, Math.min(100, progress))}%`,
          background:
            'linear-gradient(180deg, rgba(245,217,126,0.9) 0%, rgba(245,217,126,0.3) 100%)',
          boxShadow: '0 0 10px rgba(245,217,126,0.5)',
        }}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// EndMarker — микро-метка, что вы почти в конце
// ──────────────────────────────────────────────────────────────────────

function EndMarker({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return (
    <div className="mb-24 flex justify-center">
      <div
        className="inline-flex items-center gap-2 rounded-full border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-[rgba(255,255,255,0.35)]"
      >
        <motion.span
          animate={{ y: [0, 3, 0] }}
          transition={{ duration: 1.4, repeat: Infinity }}
          className="inline-block"
        >
          ↓
        </motion.span>
        до конца документа
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────

async function needsOnboardingCheck(): Promise<boolean> {
  try {
    const r = await lunaApi.onboarding.status();
    const data = r.data as { success: boolean; status?: { shouldShow: boolean } };
    return Boolean(data?.status?.shouldShow);
  } catch {
    return false;
  }
}

function useReadProgress(
  scrollRef: React.MutableRefObject<HTMLDivElement | null>,
  phase: Phase,
): number {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (phase !== 'active') return;
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const max = el.scrollHeight - el.clientHeight;
      if (max <= 0) {
        setProgress(100);
        return;
      }
      setProgress(Math.round((el.scrollTop / max) * 100));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [phase, scrollRef]);
  return progress;
}
